import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchWithAuth } from "../../utils/api";
import { TagIcon } from "../../utils/tagIcons";
import "./OsrPanelParts.css"; // its styles, wherever it is shown (a lead, a booking on Sales Intake, a Sales Sheet)

const leadPhotoUrl = (path) => `/admin/osr/image?file=${encodeURIComponent(path)}`;

// Photos are private: they are fetched with the person's sign-in and shown
// from memory. One fetch per photo per panel — the thumbnail and the full
// view share it. `urlFor` turns a photo's key into the API path that serves it
// (a lead's storage path by default; an online booking's photo id elsewhere).
function usePhoto(path, urlFor) {
  const [state, setState] = useState({ url: null, failed: false });
  // Read through a ref: a caller's new function each render must not fetch the photo again.
  const urlForRef = useRef(urlFor);
  urlForRef.current = urlFor;
  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    let made = null;
    setState({ url: null, failed: false });
    (async () => {
      try {
        const res = await fetchWithAuth(urlForRef.current(path), { skipSaveIndicator: true });
        if (!res.ok) throw new Error(`photo ${res.status}`);
        const url = URL.createObjectURL(await res.blob());
        if (cancelled) { URL.revokeObjectURL(url); return; }
        made = url;
        setState({ url, failed: false });
      } catch {
        if (!cancelled) setState({ url: null, failed: true });
      }
    })();
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path]);
  return state;
}

function Thumb({ path, index, total, onOpen, onLoaded, urlFor, alt }) {
  const { url, failed } = usePhoto(path, urlFor);
  const [unviewable, setUnviewable] = useState(false);
  useEffect(() => { if (url) onLoaded(index, url); }, [url, index, onLoaded]);
  if (failed) {
    return (
      <div className="osr-photo osr-photo-failed" role="img" aria-label={`Photo ${index + 1} could not be loaded`}>
        <TagIcon name="alert" size={16} />
        <span>Couldn't load</span>
      </div>
    );
  }
  if (!url) return <div className="osr-photo osr-photo-loading" role="img" aria-label={`Loading photo ${index + 1}`} />;
  // A photo this browser cannot draw (an iPhone HEIC outside Safari): offered as a download instead.
  if (unviewable) {
    return (
      <a className="osr-photo osr-photo-failed" href={url} download={`photo-${index + 1}.heic`} aria-label={`Download photo ${index + 1} of ${total}`}>
        <TagIcon name="document" size={16} />
        <span>Download</span>
      </a>
    );
  }
  return (
    <button type="button" className="osr-photo" onClick={() => onOpen(index)} aria-label={`Open photo ${index + 1} of ${total}`}>
      <img src={url} alt={`${alt}, ${index + 1} of ${total}`} onError={() => setUnviewable(true)} />
    </button>
  );
}

/**
 * A lead's photos: thumbnails that open full size. The full view is a real
 * dialog (Escape closes it, the arrow keys move through the photos, focus
 * returns to where it was). A photo that fails says so; it does not spin.
 */
export default function OsrPhotos({ images, urlFor = leadPhotoUrl, alt = "Lead", emptyText = "No photos came with this lead." }) {
  const [open, setOpen] = useState(null); // index
  const [urls, setUrls] = useState({});
  const closeRef = useRef(null);
  const opener = useRef(null);
  const total = images.length;

  const onLoaded = useCallback((index, url) => setUrls((u) => (u[index] === url ? u : { ...u, [index]: url })), []);
  const openAt = (index) => { opener.current = document.activeElement; setOpen(index); };
  const close = useCallback(() => {
    setOpen(null);
    if (opener.current && opener.current.focus) opener.current.focus();
  }, []);
  const step = useCallback((by) => setOpen((i) => (i === null ? i : (i + by + total) % total)), [total]);

  // Taken before anything else hears it: Escape closes the photo, not the
  // lead panel behind it.
  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
      else return;
      e.stopPropagation();
    };
    document.addEventListener("keydown", onKey, true);
    if (closeRef.current) closeRef.current.focus();
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, close, step]);

  if (!total) return emptyText ? <div className="osr-empty">{emptyText}</div> : null;

  return (
    <div className="osr-photos">
      <div className="osr-photo-grid">
        {images.map((path, i) => <Thumb key={path} path={path} index={i} total={total} onOpen={openAt} onLoaded={onLoaded} urlFor={urlFor} alt={alt} />)}
      </div>
      {open !== null && createPortal(
        <div className="osr-lightbox" role="dialog" aria-modal="true" aria-label={`Photo ${open + 1} of ${total}`} onClick={close}>
          <button type="button" ref={closeRef} className="osr-lightbox-btn osr-lightbox-close" onClick={close} aria-label="Close photo">
            <TagIcon name="x" size={20} strokeWidth={2.4} />
          </button>
          {total > 1 && (
            <button type="button" className="osr-lightbox-btn osr-lightbox-prev" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous photo">‹</button>
          )}
          {urls[open]
            ? <img className="osr-lightbox-img" src={urls[open]} alt={`${alt}, ${open + 1} of ${total}`} onClick={(e) => e.stopPropagation()} />
            : <div className="osr-lightbox-wait">Loading…</div>}
          {total > 1 && (
            <button type="button" className="osr-lightbox-btn osr-lightbox-next" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next photo">›</button>
          )}
          {total > 1 && <div className="osr-lightbox-count">{open + 1} of {total}</div>}
        </div>,
        document.body,
      )}
    </div>
  );
}
